/**
 * Benchmarks: a client's KPIs against the firm's other clients, anonymously.
 *
 * Anonymity rule: only the peer median and middle half are shown, and only when at least MIN_PEERS
 * other clients have a usable value after outliers are left out. With five or more, each figure
 * shown is a point among many (and the quartiles are averages of two), so no one client's figures
 * can be told apart. Peers are never named, and the counts shown are counts of clients, not values.
 *
 * Accuracy rules:
 * - Final figures only (kpi_snapshots.quality = 'final'), and the same months for everyone: the
 *   client's latest three final months in a row; a peer without final figures for all three of those
 *   months is left out.
 * - A ratio whose denominator is effectively zero is left out (for the client too, who then gets no
 *   position): margins and payroll % need the three months' revenue to be at least $1,000; DSO needs
 *   the sales it divides by (total revenue, or invoiced sales on that basis) to be at least $1,000;
 *   DPO needs purchases of at least $1,000; DIO needs cost of goods sold of at least $1,000; the cash
 *   conversion cycle needs all three; the
 *   current and quick ratios need current liabilities of at least $1,000; debt to equity needs equity
 *   of at least $1,000 (MIN_DENOMINATOR_CENTS).
 * - Outliers are left out per KPI before the median and quartiles are taken: values below
 *   Q1 − 1.5 × IQR or above Q3 + 1.5 × IQR (Tukey's fences, with the quartiles below). The fences are
 *   applied once, and only when MIN_PEERS or more values remain to trim from.
 * - Peer group: same industry and revenue size band when that group has at least MIN_PEERS clients,
 *   else same industry when it has MIN_PEERS, else all clients. The response says which.
 */
import { z } from "zod";
import { Exact } from "./exact";
import { rollUp, rollUpKpis, type MonthActual } from "./dashboard";
import { displayKpi, dpoPurchases, dsoSales, KPI_DEFINITIONS, type KpiKey } from "./kpis";

export const MIN_PEERS = 5;
/** $1,000: a denominator below this makes the ratio meaningless for comparison. */
export const MIN_DENOMINATOR_CENTS = 100_000;
export const BENCH_KPIS: KpiKey[] = ["gross_margin", "operating_margin", "net_margin", "current_ratio", "quick_ratio", "dso", "dpo", "dio", "cash_conversion_cycle", "debt_to_equity", "payroll_pct_revenue"];

export const INDUSTRIES = {
  food_beverage: "Food & beverage production",
  restaurants: "Restaurants & hospitality",
  healthcare: "Healthcare practices",
  construction: "Construction & trades",
  professional: "Professional services",
  retail: "Retail & e-commerce",
  manufacturing: "Manufacturing",
  real_estate: "Real estate",
  technology: "Technology & software",
  nonprofit: "Nonprofit",
  other: "Other",
} as const;
export type Industry = keyof typeof INDUSTRIES;

/** Annual revenue size bands, from the trailing 12 months. Lower bounds are inclusive. */
export const REVENUE_BANDS = {
  under_500k: "Under $500,000",
  "500k_2m": "$500,000 to $2 million",
  "2m_10m": "$2 million to $10 million",
  over_10m: "Over $10 million",
} as const;
export type RevenueBand = keyof typeof REVENUE_BANDS;

/** The size band for an annual revenue in cents: under $500k, $500k–$2M, $2M–$10M, over $10M. */
export function revenueBand(annualCents: number): RevenueBand {
  if (annualCents < 50_000_000) return "under_500k";
  if (annualCents < 200_000_000) return "500k_2m";
  if (annualCents < 1_000_000_000) return "2m_10m";
  return "over_10m";
}

/** Trailing-12-month revenue: the 12 months' total, or with fewer months their average × 12 (estimated). */
export function trailingRevenue(monthlyRevenueCents: number[]): { annualCents: number; months: number; estimated: boolean } | null {
  const m = monthlyRevenueCents.slice(-12);
  if (!m.length) return null;
  const total = m.reduce((a, b) => a + b, 0);
  return m.length >= 12 ? { annualCents: total, months: 12, estimated: false } : { annualCents: Exact.frac(total * 12, m.length).toCents(), months: m.length, estimated: true };
}

export type PeerGroup = "industry_band" | "industry" | "all";

/**
 * Which peers to compare with: same industry and size band when it has MIN_PEERS clients, else same
 * industry when it has MIN_PEERS, else all clients.
 */
export function choosePeerGroup(counts: { industryBand: number | null; industry: number | null }): PeerGroup {
  if (counts.industryBand !== null && counts.industryBand >= MIN_PEERS) return "industry_band";
  if (counts.industry !== null && counts.industry >= MIN_PEERS) return "industry";
  return "all";
}

const median = (sorted: Exact[]): Exact => {
  const n = sorted.length;
  return n % 2 ? sorted[(n - 1) / 2]! : sorted[n / 2 - 1]!.add(sorted[n / 2]!).div(Exact.int(2));
};

/** Median and quartiles (the medians of the lower and upper halves, middle value excluded). Needs at least 3 values; benchmarks use it from MIN_PEERS. */
export function quartiles(values: Exact[]): { q1: Exact; median: Exact; q3: Exact } {
  if (values.length < 3) throw new RangeError("At least three values are needed");
  const s = [...values].sort((a, b) => a.cmp(b));
  const half = Math.floor(s.length / 2);
  return { q1: median(s.slice(0, half)), median: median(s), q3: median(s.slice(s.length - half)) };
}

/** Leaves out values outside Q1 − 1.5 × IQR and Q3 + 1.5 × IQR (only from MIN_PEERS values up). */
export function trimOutliers(values: Exact[]): { kept: Exact[]; removed: number } {
  if (values.length < MIN_PEERS) return { kept: values, removed: 0 };
  const q = quartiles(values);
  const reach = q.q3.sub(q.q1).mul(Exact.frac(3, 2));
  const lo = q.q1.sub(reach);
  const hi = q.q3.add(reach);
  const kept = values.filter((v) => v.cmp(lo) >= 0 && v.cmp(hi) <= 0);
  return { kept, removed: values.length - kept.length };
}

/** The figures a ratio divides by, for the benchmark months (flows summed, balances at the last month-end). */
export type BenchBases = {
  revenue: number;
  cogs: number;
  currentLiabilities: number;
  equity: number;
  inventory?: number;
  /** What DSO divided by (total revenue, or invoiced sales on that basis). */
  dsoSales?: number | null;
  /** What DPO divided by (cost of goods sold + operating expenses − payroll − depreciation). */
  purchases?: number | null;
};

/** Below this many peers a comparison is shown, but marked as resting on a small group. */
export const RELIABLE_PEERS = 10;

/** Benchmark windows: the latest 3 final months, or the latest 12 (evens out seasonal businesses). */
export const BENCH_WINDOWS = [3, 12] as const;
export type BenchWindow = (typeof BENCH_WINDOWS)[number];

/**
 * A published figure the firm enters for an industry (from a source it licenses or a public one),
 * shown beside the firm's own peers. In the KPI's own units: a fraction for percentages (0.385),
 * days, or a ratio.
 */
export type BenchReference = { value: string; source: string; year: number; url?: string | null };

const DENOMINATORS: Record<string, Array<Exclude<keyof BenchBases, "inventory">>> = {
  gross_margin: ["revenue"],
  operating_margin: ["revenue"],
  net_margin: ["revenue"],
  dso: ["dsoSales"],
  payroll_pct_revenue: ["revenue"],
  dpo: ["purchases"],
  dio: ["cogs"],
  cash_conversion_cycle: ["dsoSales", "cogs", "purchases"],
  current_ratio: ["currentLiabilities"],
  quick_ratio: ["currentLiabilities"],
  debt_to_equity: ["equity"],
};

/**
 * Why a KPI's denominator can't be compared, if it can't: a negative one (equity for debt to
 * equity, or revenue or cost of goods sold after credits) makes the ratio meaningless; one under
 * MIN_DENOMINATOR_CENTS is too small. No bases: assume it can.
 */
export function denominatorIssue(key: KpiKey, bases: BenchBases | null | undefined): "negative" | "small" | null {
  if (!bases) return null;
  const used = DENOMINATORS[key] ?? [];
  if (used.some((b) => (bases[b] ?? 0) < 0)) return "negative";
  return used.some((b) => (bases[b] ?? 0) < MIN_DENOMINATOR_CENTS) ? "small" : null;
}

/** Whether a KPI's denominator is positive and large enough to compare. */
export function denominatorOk(key: KpiKey, bases: BenchBases | null | undefined): boolean {
  return denominatorIssue(key, bases) === null;
}

/** One client's KPI values and denominators for the benchmark months. */
export type BenchSubject = { values: Partial<Record<KpiKey, string | null>>; bases?: BenchBases | null };

/** A client's benchmark figures from its months (oldest first), as the KPI library computes them. */
export function benchSubject(months: MonthActual[]): BenchSubject {
  const k = rollUpKpis(months);
  const i = rollUp(months);
  return {
    values: Object.fromEntries(BENCH_KPIS.map((key) => [key, k[key].exact ? k[key].exact!.toString() : null])) as Partial<Record<KpiKey, string | null>>,
    bases: { revenue: i.period.revenue, cogs: i.period.cogs, currentLiabilities: i.balance.currentLiabilities, equity: i.balance.equity, inventory: i.balance.inventory, dsoSales: dsoSales(i), purchases: dpoPurchases(i.period) },
  };
}

export type Position = "top_quarter" | "upper_middle" | "lower_middle" | "bottom_quarter" | "above_median" | "below_median" | "at_median";

export type BenchRow = {
  key: KpiKey;
  label: string;
  /** The client's value, display text and exact. */
  value: { display: string; exact: string | null };
  /** Peers whose value was used (after leaving out small denominators and outliers). */
  peers: number;
  /** Peer values left out as outliers. */
  outliers: number;
  /** Peers left out because the ratio's denominator was under $1,000. */
  smallBase: number;
  /** Peers left out because the denominator is negative. */
  negativeBase: number;
  /** Days inventory outstanding only: peers left out because they hold no inventory. */
  noInventory: number;
  /** The published reference for the client's industry, when the firm has entered one. */
  reference: { display: string; source: string; year: number; url: string | null } | null;
  /** Null when fewer than MIN_PEERS peers have a usable value. */
  stats: { q1: string; median: string; q3: string; q1Display: string; medianDisplay: string; q3Display: string } | null;
  position: Position | null;
  /** Why the client has no position although peers are shown. */
  note: string | null;
};

/** Where the client sits among peers, with "top" meaning better when the KPI has a better direction. */
export function positionOf(v: Exact, q: { q1: Exact; median: Exact; q3: Exact }, higherIsBetter: boolean | null): Position {
  if (higherIsBetter === null) {
    const c = v.cmp(q.median);
    return c > 0 ? "above_median" : c < 0 ? "below_median" : "at_median";
  }
  // The best quarter is above the third quartile when higher is better, below the first when lower is.
  const [best, worst] = higherIsBetter ? [q.q3, q.q1] : [q.q1, q.q3];
  const atLeast = (a: Exact, b: Exact) => (higherIsBetter ? a.cmp(b) >= 0 : a.cmp(b) <= 0);
  if (atLeast(v, best)) return "top_quarter";
  if (atLeast(v, q.median)) return "upper_middle";
  if (atLeast(v, worst)) return "lower_middle";
  return "bottom_quarter";
}

/** Holds inventory at the window's last month-end (unknown counts as holding some). */
const holdsInventory = (b: BenchBases | null | undefined) => !b || b.inventory === undefined || b.inventory > 0;

export function benchmarkRows(client: BenchSubject, peers: BenchSubject[], references: Partial<Record<KpiKey, BenchReference>> = {}): BenchRow[] {
  return BENCH_KPIS.map((key) => {
    const def = KPI_DEFINITIONS[key];
    const raw = client.values[key];
    // Days inventory outstanding compares only businesses that hold inventory: a business with none
    // has no inventory days (0 would pull the median down), so it is left out, the client included.
    const dio = key === "dio";
    const mine = raw && (!dio || holdsInventory(client.bases)) ? Exact.parse(raw) : null;
    const withAny = peers.filter((p) => !!p.values[key]);
    const withValue = dio ? withAny.filter((p) => holdsInventory(p.bases)) : withAny;
    const ref = references[key];
    const usable = withValue.filter((p) => denominatorOk(key, p.bases));
    const negativeBase = withValue.filter((p) => denominatorIssue(key, p.bases) === "negative").length;
    const { kept, removed } = trimOutliers(usable.map((p) => Exact.parse(p.values[key]!)));
    const q = kept.length >= MIN_PEERS ? quartiles(kept) : null;
    const myIssue = denominatorIssue(key, client.bases);
    const mineOk = myIssue === null;
    return {
      key,
      label: def.label,
      value: { display: displayKpi(def.kind, mine), exact: mine ? mine.toString() : null },
      peers: kept.length,
      outliers: removed,
      smallBase: withValue.length - usable.length - negativeBase,
      negativeBase,
      noInventory: withAny.length - withValue.length,
      reference: ref ? { display: displayKpi(def.kind, Exact.decimal(ref.value)), source: ref.source, year: ref.year, url: ref.url ?? null } : null,
      stats: q ? { q1: q.q1.toString(), median: q.median.toString(), q3: q.q3.toString(), q1Display: displayKpi(def.kind, q.q1), medianDisplay: displayKpi(def.kind, q.median), q3Display: displayKpi(def.kind, q.q3) } : null,
      position: q && mine && mineOk ? positionOf(mine, q, def.higherIsBetter) : null,
      note: dio && raw && !holdsInventory(client.bases) ? "Not ranked: this client holds no inventory" : q && mine && !mineOk ? (myIssue === "negative" ? "Not ranked: the figure this ratio divides by is negative for this client" : "Not ranked: the figure this ratio divides by is under $1,000 for this client") : null,
    };
  });
}

// --- The firm's CFO defaults: targets and published references per industry ------------------

const exactText = z.string().trim().regex(/^-?\d{1,9}(\.\d{1,6})?$/);
const industryKey = z.enum(Object.keys(INDUSTRIES) as [Industry, ...Industry[]]);
const kpiKey = z.enum(BENCH_KPIS as [KpiKey, ...KpiKey[]]);
const targetSchema = z.object({ comparator: z.enum(["at_least", "at_most"]), target: exactText, tolerance: z.string().regex(/^0(\.\d{1,4})?$|^1(\.0{1,4})?$/).default("0.10") }).strict();
const referenceSchema = z
  .object({ value: exactText, source: z.string().trim().min(1).max(200), year: z.number().int().min(2000).max(2100), url: z.string().trim().url().startsWith("https://").max(500).nullable().optional() })
  .strict();

/**
 * Per industry: default KPI targets (copied to a client on request, then the client's own) and
 * published reference figures (shown beside the firm's peers, with their source and year). Values
 * are in the KPI's own units: a fraction for percentages, days, or a ratio.
 */
export const cfoDefaultsSchema = z
  .object({
    targets: z.record(industryKey, z.record(kpiKey, targetSchema)).default({}),
    references: z.record(industryKey, z.record(kpiKey, referenceSchema)).default({}),
  })
  .strict();
export type CfoDefaults = z.infer<typeof cfoDefaultsSchema>;

export function cfoDefaultsOf(raw: unknown): CfoDefaults {
  const p = cfoDefaultsSchema.safeParse(raw ?? {});
  return p.success ? p.data : { targets: {}, references: {} };
}
